import { z } from "zod";

import { DatabaseDefaults } from "@/src/models/database-defaults";

export enum ErrorCodeLogCategory {
  AI = "AI",
  DOCUMENT = "DOCUMENT",
  EMR_API = "EMR_API",
  EMR_WEB_AUTOMATION = "EMR_WEB_AUTOMATION",
}

export enum ErrorType {
  ATHENA_INSURANCE_ELIGIBILITY = "athena_insurance_eligibility_fail",
  CC_PATIENT_METADATA_UPDATE = "cc_patient_metadata_update_fail",
  CC_TE_CREATION = "cc_te_creation_fail",
  CLINICIENT_CASE_CONFIGURATION = "clinicient_case_configuration_fail",
  CLINICIENT_CASE_CREATION = "clinicient_case_creation_fail",
  CLINICIENT_DOCUMENT_NOTE = "clinicient_document_note_fail",
  CLINICIENT_INSURANCE_SYNC = "clinicient_insurance_sync_fail",
  DEMOGRAPHIC_DATA = "demographic_data_fail",
  /** @deprecated superseded by DOCUMENT_FETCH_PATIENT_DETAILS; kept for parsing of historical error_log rows. */
  DOCUMENT_APPOINTMENT_FETCH = "document_appointment_fetch_fail",
  DOCUMENT_CC_METADATA_SETUP = "document_cc_metadata_setup_fail",
  DOCUMENT_CC_METADATA_UPDATE = "document_cc_metadata_update_fail",
  DOCUMENT_DI_ORDER_UPDATE = "document_di_order_update_fail",
  DOCUMENT_EHR_UPLOAD = "document_ehr_upload_fail",
  DOCUMENT_FETCH_PATIENT_DETAILS = "document_fetch_patient_details_fail",
  DOCUMENT_HOSPITALIZATION_HISTORY_UPDATE = "document_hospitalization_history_update_fail",
  DOCUMENT_MAY_HAVE_BEEN_UPLOADED = "document_may_have_been_uploaded_fail",
  DOCUMENT_ORDER_DISPATCH = "document_order_dispatch_fail",
  DOCUMENT_ORDER_MATCH = "document_order_match_fail",
  DOCUMENT_OUT_OF_SCOPE_CHECK = "document_out_of_scope_check_fail",
  DOCUMENT_PREFLIGHT = "document_preflight_fail",
  DOCUMENT_REFERRAL_ATTACH = "document_referral_attach_fail",
  DOCUMENT_REFERRAL_CLOSURE = "document_referral_closure_fail",
  DOCUMENT_REFERRAL_MATCH = "document_referral_match_fail",
  DOCUMENT_REFERRAL_OPEN_CHECK = "document_referral_open_check_fail",
  DOCUMENT_REFERRAL_ORDER_CREATION = "document_referral_order_creation_fail",
  DOCUMENT_RESOLVE_ORDERING_PROVIDER = "document_resolve_ordering_provider_fail",
  DOCUMENT_SFTP_UPLOAD = "document_sftp_upload_fail",
  DOCUMENT_TEL_ENC_CREATION = "document_tel_enc_creation_fail",
  DOCUMENT_TE_ATTACH = "document_te_attach_fail",
  DOCUMENT_UPLOAD = "document_upload_fail",
  DOCUMENT_WORKFLOW_STEP_UNCAUGHT = "document_workflow_step_uncaught_fail",
  INSURANCE_LINK = "insurance_link_fail",
  METADATA_TAG = "metadata_tag_fail",
  PATIENT_CASE_CREATION = "patient_case_creation_fail",
  PATIENT_CREATION = "patient_creation_fail",
  PATIENT_MRN_UNAVAILABLE = "patient_mrn_unavailable_fail",
  PATIENT_UPDATE = "patient_update_fail",
  PROVIDER_MAPPING = "provider_mapping_fail",
  REFERRAL_TIER_DETERMINATION = "referral_tier_determination_fail",
  SCHEDULING_NOTE = "scheduling_note_fail",
  SMS_ELIGIBILITY = "sms_eligibility_fail",
}

export const fallbackMessage: Record<ErrorType, string> = {
  [ErrorType.ATHENA_INSURANCE_ELIGIBILITY]: "Athena insurance eligibility did not complete today",
  [ErrorType.CC_PATIENT_METADATA_UPDATE]: "CloudCruise patient metadata update step failed",
  [ErrorType.CC_TE_CREATION]: "CloudCruise telephone encounter creation step failed",
  [ErrorType.CLINICIENT_CASE_CONFIGURATION]: "Clinicient case configuration could not be confirmed",
  [ErrorType.CLINICIENT_CASE_CREATION]: "Clinicient case creation could not be confirmed",
  [ErrorType.CLINICIENT_DOCUMENT_NOTE]: "Clinicient document upload note could not be confirmed",
  [ErrorType.CLINICIENT_INSURANCE_SYNC]: "Clinicient primary insurance could not be confirmed",
  [ErrorType.DEMOGRAPHIC_DATA]: "Phone, Address, Sex, or Email missing/invalid",
  [ErrorType.DOCUMENT_APPOINTMENT_FETCH]: "Appointment fetch step failed",
  [ErrorType.DOCUMENT_CC_METADATA_SETUP]: "CloudCruise metadata setup step failed",
  [ErrorType.DOCUMENT_CC_METADATA_UPDATE]: "CloudCruise metadata update step failed",
  [ErrorType.DOCUMENT_DI_ORDER_UPDATE]: "Diagnostic imaging order update step failed",
  [ErrorType.DOCUMENT_EHR_UPLOAD]: "Document EHR upload step failed",
  [ErrorType.DOCUMENT_FETCH_PATIENT_DETAILS]: "Fetch patient details step failed",
  [ErrorType.DOCUMENT_HOSPITALIZATION_HISTORY_UPDATE]: "Hospitalization history update step failed",
  [ErrorType.DOCUMENT_MAY_HAVE_BEEN_UPLOADED]:
    "Document may already be in the EHR; verify before re-driving to avoid a duplicate",
  [ErrorType.DOCUMENT_ORDER_DISPATCH]: "Order dispatch step failed",
  [ErrorType.DOCUMENT_ORDER_MATCH]: "Order matching step failed",
  [ErrorType.DOCUMENT_OUT_OF_SCOPE_CHECK]: "Out-of-scope check step failed",
  [ErrorType.DOCUMENT_PREFLIGHT]: "Workflow preflight step failed",
  [ErrorType.DOCUMENT_REFERRAL_ATTACH]: "Referral attach step failed",
  [ErrorType.DOCUMENT_REFERRAL_CLOSURE]: "Referral closure step failed",
  [ErrorType.DOCUMENT_REFERRAL_MATCH]: "Referral match step failed",
  [ErrorType.DOCUMENT_REFERRAL_OPEN_CHECK]: "Referral open check step failed",
  [ErrorType.DOCUMENT_REFERRAL_ORDER_CREATION]: "Referral order creation step failed",
  [ErrorType.DOCUMENT_RESOLVE_ORDERING_PROVIDER]: "Ordering provider resolution step failed",
  [ErrorType.DOCUMENT_SFTP_UPLOAD]: "Document SFTP upload step failed",
  [ErrorType.DOCUMENT_TEL_ENC_CREATION]: "Cobalt telephone encounter creation step failed",
  [ErrorType.DOCUMENT_TE_ATTACH]: "Telephone encounter attach step failed",
  [ErrorType.DOCUMENT_UPLOAD]: "Document failed to upload to EHR",
  [ErrorType.DOCUMENT_WORKFLOW_STEP_UNCAUGHT]: "Workflow step threw an uncaught error",
  [ErrorType.INSURANCE_LINK]: "Payer/Policy data failed to attach to the referral",
  [ErrorType.METADATA_TAG]: "File is in EHR, but Name, Scanned By, or Reviewed failed",
  [ErrorType.PATIENT_CASE_CREATION]: "Patient case not created",
  [ErrorType.PATIENT_CREATION]: "New chart/file not created",
  [ErrorType.PATIENT_MRN_UNAVAILABLE]: "Chart exists but the EHR has not returned its MRN",
  [ErrorType.PATIENT_UPDATE]: "Existing file found but could not be updated",
  [ErrorType.PROVIDER_MAPPING]: "Referring/Receiving provider or staff identifier failed",
  [ErrorType.REFERRAL_TIER_DETERMINATION]: "Patient tier could not be determined",
  [ErrorType.SCHEDULING_NOTE]: "Failure to create Referral order or Telephone note",
  [ErrorType.SMS_ELIGIBILITY]: "SMS eligibility could not be determined",
};

export const errorLogSchema = z.object({
  taskId: z.string().nullable(),
  rawError: z.any().nullable().optional(),
  category: z.nativeEnum(ErrorCodeLogCategory).nullable(),
  normalizedName: z.nativeEnum(ErrorType).nullable(),
  errorMessage: z.string().nullable(),
});

export type ErrorLog = z.infer<typeof errorLogSchema> & DatabaseDefaults;

export function getActionableInstruction(errorLog: ErrorLog): string {
  if (errorLog.normalizedName === ErrorType.DEMOGRAPHIC_DATA) {
    const fieldsMatch = errorLog.errorMessage?.match(/Missing required fields: (.+)/);

    if (fieldsMatch) {
      const fields = fieldsMatch[1].split(", ");
      const formatted =
        fields.length > 1 ? `${fields.slice(0, -1).join(", ")} and ${fields[fields.length - 1]}` : fields[0];

      return `Update the patient in Athena, ensuring ${formatted} ${fields.length > 1 ? "are" : "is"} present`;
    }
  }

  switch (errorLog.normalizedName) {
    case ErrorType.ATHENA_INSURANCE_ELIGIBILITY:
      return "Run eligibility for the primary policy in Athena and verify today's result";
    case ErrorType.CLINICIENT_CASE_CONFIGURATION:
      return "Verify the saved case ID belongs to the intended patient in Clinicient, then review and complete its configuration; do not create another case";
    case ErrorType.CLINICIENT_CASE_CREATION:
      return "Check the patient’s cases in Clinicient and reconcile any existing case before attempting creation again";
    case ErrorType.CLINICIENT_DOCUMENT_NOTE:
      return "Verify the referral upload note in Clinicient before attempting another note; do not repeat the completed document upload";
    case ErrorType.CLINICIENT_INSURANCE_SYNC:
      return "Verify primary insurance on the saved Clinicient case and reconcile conflicts before resuming; do not recreate the patient or case";
    case ErrorType.DEMOGRAPHIC_DATA:
      return "Update the patient demographic fields in Athena";
    case ErrorType.DOCUMENT_MAY_HAVE_BEEN_UPLOADED:
      return "Verify in the EHR whether the document was uploaded; re-drive only if it is not already present";
    case ErrorType.DOCUMENT_UPLOAD:
      return "Upload the document to the patient's chart";
    case ErrorType.INSURANCE_LINK:
      return "Attach the payer/policy to the patient's chart";
    case ErrorType.METADATA_TAG:
      return "Update the document name, scanned by, or reviewed fields in Athena";
    case ErrorType.PATIENT_CASE_CREATION:
      return "Create the patient case in Athena";
    case ErrorType.PATIENT_CREATION:
      return "Create the patient chart in Athena";
    case ErrorType.PATIENT_UPDATE:
      return "Update the existing patient record in Athena";
    case ErrorType.PROVIDER_MAPPING:
      return "Verify and set the referring/receiving provider in Athena";
    case ErrorType.REFERRAL_TIER_DETERMINATION:
      return "Review the insurance snapshot, receiving location and referral details before determining the patient tier";
    case ErrorType.SCHEDULING_NOTE:
      return "Create the referral order or telephone note in Athena";
    case ErrorType.SMS_ELIGIBILITY:
      return "Review the patient tier, matched-case appointments and outreach history before sending SMS";
    default:
      return "Update this record in the EHR";
  }
}

export const errorLogShape: ErrorLog = {
  id: "",
  taskId: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  groupId: "",
  rawError: null,
  normalizedName: null,
  errorMessage: null,
  category: null,
};
